import { trpc } from '@/trpcClient';
import { useHasScopes } from '@/hooks/useHasScopes';
import type { ListRoleOutputDto, RoleDto } from '@shelflix/trpc/routes/role/list/useCase';
import Icon from '@shelflix/ui-web/components/Icon';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export function RoleList({
  roles,
  onRoleChanged,
}: {
  roles: ListRoleOutputDto;
  onRoleChanged: (role: { uuid: string }) => unknown;
}) {
  const navigate = useNavigate();
  const canEditRoles = useHasScopes(['admin:roles:edit']);

  const editRoleMutation = useMutation(trpc.role.edit.mutationOptions());
  const deleteRoleMutation = useMutation(trpc.role.delete.mutationOptions());

  const handlePositionChange = (role: RoleDto, change: 1 | -1) => {
    editRoleMutation.mutate(
      { ...role, hierarchy: role.hierarchy + change, permissions: role.permissions.map((p) => p.uuid) },
      {
        onSuccess: (result) => {
          onRoleChanged(result);
          toast.success('Cargo editado com sucesso');
        },
        onError: (error) => {
          toast.error(`Erro ao editar cargo: ${error.message}`);
        },
      }
    );
  };

  const handleDeleteRole = (role: RoleDto) => {
    deleteRoleMutation.mutate(
      { uuid: role.uuid },
      {
        onSuccess: (_result) => {
          onRoleChanged({ uuid: role.uuid });
          toast.success('Cargo removido com sucesso');
        },
        onError: (error) => {
          toast.error(`Erro ao remover cargo: ${error.message}`);
        },
      }
    );
  };

  return (
    <section className="flex flex-col gap-4">
      {roles.map((role, index) => {
        const isAdmin = index === 0;
        const isLast = index === roles.length - 1;
        const isSecondLast = index === roles.length - 2;

        const isSecond = index === 1;
        // Can only edit/delete if not Admin role AND user has admin:roles:edit permission
        const canEdit = !isAdmin && canEditRoles;
        const canGoUp = canEdit && !isSecond && !isLast;
        const canGoDown = canEdit && !isLast && !isSecondLast;
        return (
          <div className="border-gray flex border-2 px-5 py-3" key={'role-' + index}>
            <div className="flex flex-col py-1">
              <span>
                {index + 1}. {role.name}
              </span>
              <div className="mt-auto flex gap-2">
                <Icon
                  name="expand_circle_up"
                  onClick={() => canGoUp && handlePositionChange(role, -1)}
                  size={32}
                  className={`${canGoUp ? 'cursor-pointer text-[var(--primary)]' : 'cursor-not-allowed text-[var(--gray-secondary)]'}`}
                />
                <Icon
                  name="expand_circle_down"
                  onClick={() => canGoDown && handlePositionChange(role, 1)}
                  size={32}
                  className={`${canGoDown ? 'cursor-pointer text-[var(--primary)]' : 'cursor-not-allowed text-[var(--gray-secondary)]'}`}
                />
              </div>
            </div>
            <div className="ml-auto flex flex-col gap-1 text-end">
              <div className="ml-auto flex gap-1 py-1">
                <Icon
                  name="delete"
                  size={24}
                  onClick={() => canEdit && handleDeleteRole(role)}
                  className={`${canEdit ? 'cursor-pointer text-[var(--primary)]' : 'cursor-not-allowed text-[var(--gray-secondary)]'}`}
                />
                <Icon
                  name="edit"
                  onClick={() => canEdit && navigate(`/role/${role.uuid}`)}
                  size={24}
                  className={`${canEdit ? 'cursor-pointer text-[var(--primary)]' : 'cursor-not-allowed text-[var(--gray-secondary)]'}`}
                />
              </div>
              <span>{role.userCount} usuários</span>
              <span>{role.permissionCount} permissões</span>
            </div>
          </div>
        );
      })}
    </section>
  );
}
